/**
 * OnayMetniGovdesi.jsx — bir onay metninin GÖVDESİ: üstte HTML (boş olabilir), altında sırayla dosyalar (backlog #553, backend #552).
 *
 * Dosya PDF ise sayfaları alt alta (canvas), görsel ise olduğu gibi çizilir; hepsi çağıranın kaydırma kutusunun İÇİNDE durur. Böylece
 * "Onaylıyorum metin sonuna kadar kaydırılınca açılır" kuralı (OnayMetniPencereleri.sonundaMi) HTML metinde olduğu gibi çalışır.
 * Dosyalar oturum başlıklarıyla BLOB olarak okunur (<img>/<iframe> başlık gönderemez). pdf.js yalnızca PDF varsa yüklenir.
 *
 * Çağıran `onDurum` ile toplam durumu öğrenir: 'yukleniyor' (kutu henüz son yüksekliğinde DEĞİL: "sona gelindi" ölçümü bekler),
 * 'hazir' ya da 'hata' (okunamayan metni kimse onaylamasın). Dosyası olmayan gövde `onDurum` çağırmaz.
 * Hem Fuar Kuralları / geçit penceresi (yayınlanmış sürüm) hem yönetim önizlemesi (taslak, yalnızca personel) kullanır.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import GuvenliHtml from "./GuvenliHtml";
import { DOSYA_TURU, getDosya } from "../../services/onayMetniService";

export const DOSYA_DURUMU = { YUKLENIYOR: "yukleniyor", HAZIR: "hazir", HATA: "hata" };

/** Görünür alana bu kadar piksel kala PDF sayfası çizilir (alttaki sayfalar kaydırıldıkça, hepsi birden değil). */
const ONCEDEN_CIZ = "800px 0px";

/** Bir PDF sayfası: boyutu baştan bilinir (aspect-ratio), bu yüzden kutunun toplam yüksekliği çizimden ÖNCE de doğrudur. */
function PdfSayfa({ pdf, no, boyut, onHata }) {
  const kap = useRef(null);
  const tuval = useRef(null);

  useEffect(() => {
    const el = kap.current;
    if (!el) return undefined;
    let basladi = false;
    async function ciz() {
      if (basladi) return;
      basladi = true;
      try {
        await pdf.sayfaCiz(no, tuval.current, el.clientWidth);
      } catch (e) {
        onHata(e);
      }
    }
    if (typeof IntersectionObserver === "undefined") {
      ciz();
      return undefined;
    }
    const gozlem = new IntersectionObserver((kayitlar) => {
      if (kayitlar.some((k) => k.isIntersecting)) {
        gozlem.disconnect();
        ciz();
      }
    }, { rootMargin: ONCEDEN_CIZ });
    gozlem.observe(el);
    return () => gozlem.disconnect();
  }, [pdf, no, onHata]);

  return (
    <div ref={kap} className="onay-pdf-sayfa" style={{ aspectRatio: `${boyut.genislik} / ${boyut.yukseklik}` }} data-testid={`pdf-sayfa-${no}`}>
      <canvas ref={tuval} />
    </div>
  );
}

/** Tek dosya: indirir, PDF'i açar ya da görseli gösterir; durumunu `onDurum(dosyaId, durum)` ile bildirir. */
function DosyaGorunumu({ dosya, onDurum }) {
  const { t } = useTranslation("portal");
  const [durum, setDurum] = useState(DOSYA_DURUMU.YUKLENIYOR);
  const [hata, setHata] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [resimUrl, setResimUrl] = useState(null);
  const [deneme, setDeneme] = useState(0);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let iptal = false;
    let acilan = null;
    let url = null;
    async function oku() {
      setDurum(DOSYA_DURUMU.YUKLENIYOR);
      setHata(null);
      setPdf(null);
      setResimUrl(null);
      try {
        const { veri, tip } = await getDosya(dosya.dosyaId);
        if (iptal) return;
        if (dosya.tur === DOSYA_TURU.PDF) {
          const modul = await import("../../services/pdfOku");
          const belge = await modul.pdfAc(veri);
          if (iptal) { belge.kapat(); return; }
          acilan = belge;
          setPdf(belge);
          setDurum(DOSYA_DURUMU.HAZIR);
        } else {
          url = URL.createObjectURL(new Blob([veri], { type: tip || "image/png" }));
          setResimUrl(url);   // yüklenince <img onLoad> 'hazir' yapar: yükseklik ondan önce belli değil
        }
      } catch (e) {
        if (!iptal) { setHata(e?.message || ""); setDurum(DOSYA_DURUMU.HATA); }
      }
    }
    oku();
    return () => {
      iptal = true;
      if (acilan) acilan.kapat();
      if (url) URL.revokeObjectURL(url);
    };
  }, [dosya.dosyaId, dosya.tur, deneme]);

  useEffect(() => {
    onDurum(dosya.dosyaId, durum);
    return () => onDurum(dosya.dosyaId, null);
  }, [dosya.dosyaId, durum, onDurum]);

  const sayfaHatasi = useCallback((e) => { setHata(e?.message || ""); setDurum(DOSYA_DURUMU.HATA); }, []);

  if (durum === DOSYA_DURUMU.HATA) {
    return (
      <Alert color="warning" className="small d-flex flex-wrap align-items-center gap-2" data-testid={`dosya-hata-${dosya.dosyaId}`}>
        <span className="flex-grow-1">{t("fuarKurallari.dosyaYuklenemedi", { ad: dosya.orijinalAd })}{hata ? ` ${hata}` : ""}</span>
        <Button color="light" size="sm" onClick={() => setDeneme((n) => n + 1)}>{t("onayKapisi.tekrarDene")}</Button>
      </Alert>
    );
  }

  return (
    <div className="onay-dosya" data-testid={`dosya-${dosya.dosyaId}`}>
      {durum === DOSYA_DURUMU.YUKLENIYOR && (
        <div className="text-center text-muted small py-4"><Spinner size="sm" className="me-2" />{t("fuarKurallari.dosyaYukleniyor")}</div>
      )}
      {pdf && pdf.boyutlar.map((b, i) => <PdfSayfa key={i + 1} pdf={pdf} no={i + 1} boyut={b} onHata={sayfaHatasi} />)}
      {resimUrl && (
        <img src={resimUrl} alt={dosya.orijinalAd} className="onay-dosya-gorsel" hidden={durum !== DOSYA_DURUMU.HAZIR}
          onLoad={() => setDurum(DOSYA_DURUMU.HAZIR)}
          onError={() => { setHata(""); setDurum(DOSYA_DURUMU.HATA); }} />
      )}
    </div>
  );
}

/** Bir dilin dosyaları sırayla; toplam durum `onDurum`a gider. */
export function OnayMetniDosyalari({ dosyalar, onDurum }) {
  const [durumlar, setDurumlar] = useState({});
  const bildir = useCallback((id, d) => setDurumlar((o) => {
    if (d == null) {
      if (!(id in o)) return o;
      const kalan = { ...o };
      delete kalan[id];
      return kalan;
    }
    return o[id] === d ? o : { ...o, [id]: d };
  }), []);

  const toplam = useMemo(() => {
    const idler = dosyalar.map((d) => d.dosyaId);
    if (idler.some((id) => durumlar[id] === DOSYA_DURUMU.HATA)) return DOSYA_DURUMU.HATA;
    if (idler.every((id) => durumlar[id] === DOSYA_DURUMU.HAZIR)) return DOSYA_DURUMU.HAZIR;
    return DOSYA_DURUMU.YUKLENIYOR;
  }, [dosyalar, durumlar]);

  useEffect(() => { if (onDurum) onDurum(toplam); }, [toplam, onDurum]);

  return (
    <div className="onay-dosyalar">
      {dosyalar.map((d) => <DosyaGorunumu key={d.dosyaId} dosya={d} onDurum={bildir} />)}
    </div>
  );
}

/** HTML + dosyalar. `icerik` boş ve dosya yoksa hiçbir şey çizmez. */
export default function OnayMetniGovdesi({ icerik, dosyalar, onDurum }) {
  const liste = dosyalar || [];
  return (
    <>
      {String(icerik ?? "").trim() !== "" && <GuvenliHtml html={icerik} />}
      {liste.length > 0 && <OnayMetniDosyalari dosyalar={liste} onDurum={onDurum} />}
    </>
  );
}
